import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorityBadges } from "@/components/AuthorityBadges";
import { Mail, Clock, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { MagicMyMessaging } from "@/components/MagicMyMessaging";
import { SiteHeader, SiteFooter } from "@/components/blog/SiteHeader";
import sierraStairs from "@/assets/sierra-stairs.webp";

const bookingUrl = "/book";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — Three Day Launch" },
      { name: "description", content: "Get in touch with Three Day Launch about your custom website. We respond within 24 business hours." },
      { property: "og:title", content: "Contact — Three Day Launch" },
      { property: "og:description", content: "Get in touch with Three Day Launch. We respond within 24 business hours." },
      { property: "og:url", content: "https://threedaylaunch.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", business: "", message: "" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (form.name.trim().length < 1) return toast.error("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (form.message.trim().length < 10) return toast.error("Message must be at least 10 characters.");

    setSubmitting(true);
    try {
      const res = await fetch("/api/public/contact-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          business: form.business.trim() || null,
          message: form.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to send");
      toast.success("Success! Your message has been sent.");
      setForm({ name: "", email: "", business: "", message: "" });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email hello@threedaylaunch.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader showAbout />

      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/10 via-background to-background">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:48px_48px]"
        />
        <div className="relative mx-auto max-w-3xl px-6 pt-20 md:pt-24 pb-16 md:pb-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
              <Mail className="h-3.5 w-3.5" /> Get in touch
            </div>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight">Let's talk about your coaching website</h1>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Tell us a bit about your business and what you're looking for. We'll get back to you within{" "}
              <span className="font-semibold text-foreground">24 business hours</span>.
            </p>
          </div>

          <div className="mt-10 bg-card/90 backdrop-blur rounded-3xl border border-border shadow-card-soft p-6 md:p-10">
            {submitted && (
              <div
                role="status"
                aria-live="polite"
                className="mb-6 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/10 p-4"
              >
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-foreground">Success! Your message has been sent.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    We'll be in touch within 24 business hours.
                  </p>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" name="name" value={form.name} onChange={handleChange} required maxLength={100} placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={255} placeholder="you@example.com" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="business">Business (optional)</Label>
                <Input id="business" name="business" value={form.business} onChange={handleChange} maxLength={150} placeholder="Your business name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" name="message" value={form.message} onChange={handleChange} required minLength={10} maxLength={2000} rows={6} placeholder="Tell us about your project..." />
              </div>
              <Button type="submit" size="lg" disabled={submitting} className="w-full md:w-auto">
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : "Send message"}
              </Button>
            </form>
          </div>
        </div>
      </section>



      <MeetFounder />
      <FAQ />

      <MagicMyMessaging />
      <SiteFooter />
    </div>
  );
}

function MeetFounder() {
  return (
    <section id="meet-founder" className="py-24 bg-background scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14 items-start">
          <div className="relative rounded-3xl overflow-hidden shadow-card-soft bg-muted">
            <img
              src={sierraStairs}
              alt="Sierra Janisse, Founder and Designer at Three Day Launch, sitting on stairs"
              className="w-full h-full object-cover"
              width={900}
              height={1125}
              loading="lazy"
              decoding="async"
            />
          </div>

          <div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              Hi, I'm Sierra Janisse.
            </h2>
            <div className="mt-2 text-primary font-semibold text-sm uppercase tracking-wider">
              Founder & Design Lead
            </div>

            <div className="mt-6 space-y-5 text-muted-foreground text-lg leading-relaxed">
              <p>
                I'm the founder of Three Day Launch and the creative director behind every site we launch. When you work with us, you work directly with me. No handoffs, no junior designers, no agency middlemen.
              </p>
              <p>
                I've launched hundreds of websites for businesses across every industry, from coaches and consultants to interior designers and hair stylists, accountants and dog groomers. Along the way, I noticed the same thing kept happening: brilliant coaches were stuck with websites that didn't match the quality of their work, because the "good" option always seemed to cost thousands and take months.
              </p>
              <p>
                As a Chicago-based website designer working with coaches across the globe, I kept seeing the same two extremes: a Squarespace or Wix template that looked like every other coach online, or a five-figure agency build that took six months to launch. Neither served the coaches I wanted to work with. The ones doing real, life-changing work with their clients.
              </p>
              <p>
                I built Three Day Launch as a genuine third option: a fully custom alternative to coaching templates, designed by a human, delivered in 72 hours, at a price that actually makes sense for a growing practice. See <Link to="/services" className="text-primary underline-offset-4 hover:underline font-medium">what's included in a 3-day launch</Link>, browse <Link to="/" hash="jealous" className="text-primary underline-offset-4 hover:underline font-medium">recent coaching website projects</Link>, or read more about our <Link to="/web-design-for-coaches" className="text-primary underline-offset-4 hover:underline font-medium">approach to web design for coaches</Link>.
              </p>
              <p>
                I started Three Day Launch because I believe every coach deserves a website they're genuinely proud of, without a 6-week timeline or a five-figure invoice. Based in Chicago, working with clients worldwide.
              </p>
            </div>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
              >
                <a href={bookingUrl}>
                  Work with Sierra <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            <AuthorityBadges />
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    {
      q: "What types of coaching websites do you design?",
      a: "We've launched hundreds of successful websites for coaches, consultants, and online business owners. If you sell your expertise either 1-to-1 or one-to-many and need a professional online presence so clients can learn more about you online, this is for you.\n\nThis is NOT for you if you need an e-commerce website, or your main business is selling physical products.\n",
    },
    {
      q: "Do you really launch in 3 days?",
      a: "Yes. Day one is your kick off call where we meet over Zoom and chat face to face to get your project started. \n\nDay two is design day. No live calls this day, but you will need to be available to provide feedback on the designs as we build them for you. By the end of day two, your coaching website design is DONE!\n\nOn, day three we connect your domain (like yourbusiness.com) and set up hosting for you so that you're officially live. It's celebration time!",
    },
    {
      q: "What about my website domain?",
      a: "You will need to purchase your website domain (like mybusiness.com) separately from a domain registrar. We recommend GoDaddy because it's easy, and only about $20 per year for your domain.",
    },
    {
      q: "What about hosting?",
      a: "We'll take care of hosting setup for you as part of your launch so you never have to worry about keeping your website online and secure.",
    },
    {
      q: "How much does coach website design cost?",
      a: (
        <>
          We're passionate about making coach website design affordable as well as high-touch so that nothing keeps you from reaching more of the clients who need your expertise.
          {"\n\n"}
          Your pricing is determined based on your specific website needs (number of pages needed, and content transfers, etc.) We offer payment plan options, and regularly run seasonal specials on new websites (ask about our seasonal specials <Link to="/contact" className="text-primary underline underline-offset-2 hover:opacity-80">here</Link>). For most coaching websites, you can expect to pay less than $1k USD.
        </>
      ),
    },
    {
      q: "Do you help with ongoing updates and maintenance long term?",
      a: "Yes! We don't just build you a beautiful website and then ditch you to \"figure it out\" post launch. After your website build is complete, we'll be only a message away should you need any updates or help.\n\nAfter all, your website should evolve with your brand as you continue to scale!",
    },
  ];
  return (
    <section id="faq" className="bg-muted/40 py-20">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            FAQ
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold">Questions? We've got answers.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`i-${i}`} className="bg-card rounded-2xl border border-border px-6 data-[state=open]:shadow-card-soft">
              <AccordionTrigger className="text-left font-semibold text-base hover:no-underline">{item.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground whitespace-pre-line">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12 text-center">
          <Button
            asChild
            size="lg"
            className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center"
          >
            <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </section>
  );
}
